import { Link } from "react-router-dom";
import { ArrowRight, Store, MessagesSquare, Server, Activity, ShieldCheck, Check, Code2, Building2, User, Bot } from "lucide-react";
import { Logo } from "@/components/Common";
import { BACKEND } from "@/lib/api";

export const EARTH = "https://images.unsplash.com/photo-1634176866089-b633f4aec882?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const PROVIDERS = ["Groq", "Google Gemini", "OpenRouter", "Hugging Face", "Cloudflare Workers AI", "Ollama", "vLLM", "OpenAI (kostenpflichtig)"];
const FEATURES = [
  { icon: Store, title: "API Marketplace", text: "Geprüfter Katalog freier, lokaler und kostenpflichtiger Routen – mit Quelle und Prüfdatum.", c: "from-blue-500 to-blue-700" },
  { icon: MessagesSquare, title: "AI Playground", text: "Modelle testen und sehen, welche Route wirklich genutzt wurde.", c: "from-violet-500 to-fuchsia-600" },
  { icon: Code2, title: "Ein Gateway", text: "Ein OpenAI-kompatibler Endpunkt für alle eingerichteten Provider.", c: "from-sky-500 to-indigo-600" },
  { icon: Server, title: "Ollama & vLLM", text: "Lokale Modelle ohne Cloud-Key – Daten bleiben bei dir.", c: "from-emerald-500 to-teal-600" },
  { icon: Activity, title: "Credit-Schutz", text: "Tageslimits, Verbrauchsanzeige und kein stilles Ausweichen auf bezahlte Routen.", c: "from-indigo-500 to-violet-700" },
];
const CODE = `import requests

r = requests.post(
    "${BACKEND}/api/v1/chat/completions",
    headers={"Authorization": "Bearer chub_DEIN_HUB_KEY"},
    json={
        "model": "auto",          # kostenlose/lokale Route zuerst
        "messages": [{"role": "user", "content": "Erkläre Quantencomputing."}],
    },
)
print(r.json()["casi"]["route"])`;

const Hero = () => (
  <section className="relative overflow-hidden">
    <img src={EARTH} alt="" className="absolute right-[-10%] top-[-10%] w-[70%] max-w-[1000px] opacity-60 pointer-events-none select-none" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#060a18] via-[#060a18]/85 to-transparent" />
    <div className="relative mx-auto max-w-7xl px-6 pb-24 pt-16 lg:pt-24">
      <div className="inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-4 py-1.5 text-sm text-violet-200 fade-up">
        Free-First AI Gateway von Casi AI Labs
      </div>
      <h1 className="mt-8 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-white fade-up" style={{ animationDelay: ".05s" }}>
        Alle KI-APIs.<br />Ein Hub. <span className="grad-text">Kostenlos zuerst.</span>
      </h1>
      <p className="mt-6 max-w-xl text-base md:text-lg text-slate-300 fade-up" style={{ animationDelay: ".1s" }}>
        Casi AI Hub bündelt freie Provider-Kontingente, lokale Modelle und – nur wenn du es ausdrücklich willst – kostenpflichtige APIs hinter einem einheitlichen Gateway.
      </p>
      <div className="mt-10 flex flex-wrap gap-4 fade-up" style={{ animationDelay: ".15s" }}>
        <Link to="/register" data-testid="hero-start-button" className="btn-grad inline-flex items-center gap-2 px-7 py-3.5">Kostenlos starten <ArrowRight className="h-4 w-4" /></Link>
        <Link to="/login" data-testid="hero-login-button" className="btn-ghost inline-flex items-center gap-2 px-7 py-3.5 text-white">Anmelden</Link>
      </div>
      <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
        {["Open Source bereit", "Self-hosted mit Docker", "Keine Kreditkarte nötig", "Eigene Provider-Keys"].map((t) => (
          <span key={t} className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-violet-400" />{t}</span>
        ))}
      </div>
    </div>
  </section>
);

export default function Landing() {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
        <Logo />
        <nav className="hidden md:flex gap-8 text-sm text-slate-300">
          <a href="#features" className="hover:text-white">Funktionen</a>
          <a href="#providers" className="hover:text-white">Provider</a>
          <a href="#api" className="hover:text-white">Dokumentation</a>
        </nav>
        <div className="flex gap-3">
          <Link to="/login" data-testid="nav-login-button" className="btn-ghost px-4 py-2 text-sm text-white">Anmelden</Link>
          <Link to="/register" data-testid="nav-register-button" className="btn-grad px-4 py-2 text-sm">Jetzt starten</Link>
        </div>
      </header>
      <Hero />
      <section id="providers" className="mx-auto max-w-7xl px-6">
        <div className="glass flex flex-wrap items-center justify-center gap-x-10 gap-y-4 px-6 py-5 text-sm font-semibold text-slate-300">
          {PROVIDERS.map((p) => <span key={p} className={p.includes("kostenpflichtig") ? "text-red-300/80" : ""}>{p}</span>)}
        </div>
      </section>
      <section id="features" className="mx-auto grid max-w-7xl gap-5 px-6 py-20 sm:grid-cols-2 lg:grid-cols-5">
        {FEATURES.map((f, i) => (
          <div key={f.title} className="glass p-6 transition-transform hover:-translate-y-1 fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
            <div className={`mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${f.c}`}><f.icon className="h-6 w-6 text-white" /></div>
            <h3 className="text-lg font-semibold text-white">{f.title}</h3>
            <p className="mt-2 text-sm text-slate-400">{f.text}</p>
          </div>
        ))}
      </section>
      <section id="api" className="mx-auto grid max-w-7xl gap-6 px-6 pb-24 lg:grid-cols-5">
        <div className="glass overflow-hidden lg:col-span-3">
          <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3 text-xs text-slate-400">
            <span className="h-3 w-3 rounded-full bg-red-400" /><span className="h-3 w-3 rounded-full bg-amber-400" /><span className="h-3 w-3 rounded-full bg-emerald-400" />
            <span className="ml-3">Python – OpenAI-kompatibel</span>
          </div>
          <pre className="overflow-x-auto p-5 text-[13px] leading-relaxed text-slate-300" data-testid="landing-code-sample">{CODE}</pre>
        </div>
        <div className="glass relative overflow-hidden p-8 lg:col-span-2">
          <h2 className="text-2xl font-bold text-white">Bereit für deine KI-Projekte?</h2>
          <p className="mt-3 text-slate-400 text-sm">Freie Kontingente haben Limits und können sich ändern. Der Hub zeigt sie ehrlich an – und schützt dich vor ungewollten Kosten.</p>
          <div className="mt-6 grid grid-cols-2 gap-3 text-sm text-slate-300">
            {[[Code2, "Entwickler"], [Bot, "KI-Agenten"], [Building2, "Unternehmen"], [User, "Einzelpersonen"]].map(([I, t]) => (
              <span key={t} className="inline-flex items-center gap-2"><I className="h-4 w-4 text-blue-400" />{t}</span>
            ))}
          </div>
          <Link to="/register" data-testid="cta-start-button" className="btn-grad mt-8 inline-flex items-center gap-2 px-6 py-3">Kostenlos starten <ArrowRight className="h-4 w-4" /></Link>
          <ShieldCheck className="absolute -bottom-6 -right-6 h-40 w-40 text-violet-500/10" />
        </div>
      </section>
      <footer className="border-t border-white/5 py-8 text-center text-xs text-slate-500">© Casi AI Labs · Casi AI Hub – Free-First AI Gateway</footer>
    </div>
  );
}
